import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Play, Pause, RotateCcw, RotateCw, Volume2, VolumeX } from 'lucide-react';
import CosmicBackground from '../components/CosmicBackground';
import Watermark from '../components/Watermark';

const StoryVideo = () => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasRealAudio, setHasRealAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');

  useEffect(() => {
    const healthAudio = localStorage.getItem('health_audio_url');
    console.log('Health audio URL:', healthAudio);
    
    if (healthAudio) {
      setHasRealAudio(true);
      setAudioUrl(healthAudio);
      
      // Auto-play after 1 second
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }, 1000);
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setTimeout(() => navigate('/dashboard'), 1000);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [navigate]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const handleRewind = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, currentTime - 10);
    }
  };

  const handleForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, currentTime + 10);
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = pos * duration;
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative h-screen w-full bg-black overflow-hidden">
      <div className="absolute inset-0">
        <CosmicBackground showAstroChart={true}>
          <div className="absolute inset-0 flex items-center justify-center py-20">
            {hasRealAudio && (
              <div className="text-center max-w-2xl">
                {/* Title and Text ABOVE */}
                <h2 className="text-white text-3xl font-bold mb-2" style={{letterSpacing: '0.1em'}}>
                  🔮 SAGE VAARTA 🔮
                </h2>
                <p className="text-[#D4AF37] text-xl font-semibold mb-2">
                  Your Future Health Story
                </p>
                <p className="text-gray-300 text-sm mb-6">
                  {isPlaying ? '✨ The wise sage speaks your destiny... ✨' : 'Press play to hear your future'}
                </p>
                
                {/* Sage Avatar BELOW Text */}
                <div className="relative w-64 h-64 mx-auto">
                  {/* Glowing border effect */}
                  <div className="absolute inset-0 rounded-full border-4 border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.6)] animate-pulse" />
                  <div className="absolute inset-2 rounded-full border-2 border-white/30" />
                  
                  {/* Sage Image */}
                  <img 
                    src="/sage-final.png" 
                    alt="Sage Vaarta" 
                    className={`absolute inset-0 w-full h-full object-contain p-4 drop-shadow-2xl ${isPlaying ? 'animate-pulse' : ''}`}
                    style={{
                      filter: 'brightness(1.2) contrast(1.1) saturate(1.2)',
                    }}
                  />
                  
                  {/* Audio visualization when playing */}
                  {isPlaying && (
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-1">
                      {[0, 150, 300, 450, 600].map((delay, i) => (
                        <div 
                          key={i} 
                          className="w-2 h-8 bg-[#D4AF37] rounded-full animate-bounce"
                          style={{animationDelay: `${delay}ms`}}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {!hasRealAudio && (
              <div className="text-center">
                <h2 className="text-white text-4xl font-serif mb-4">
                  No Audio Available
                </h2>
                <p className="text-gray-400">
                  Please complete the prediction first
                </p>
              </div>
            )}
          </div>
        </CosmicBackground>
      </div>

      {/* Audio Element */}
      <audio 
        ref={audioRef} 
        src={audioUrl ? `${process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app'}${audioUrl}` : ''}
        className="hidden"
      />

      <Watermark position="bottom-right" opacity={0.2} />

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 to-transparent z-50">
        <div className="flex justify-between items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="text-white p-2 hover:bg-white/30 rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <button 
            onClick={handleSkip}
            className="text-white px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors text-sm font-medium"
          >
            Skip
          </button>
        </div>
      </div>

      {/* Bottom Controls */}
      {showControls && hasRealAudio && (
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent z-50">
          {/* Progress Bar */}
          <div 
            className="w-full h-1 bg-white/30 rounded-full mb-4 cursor-pointer"
            onClick={handleSeek}
          >
            <div 
              className="h-full bg-[#D4AF37] rounded-full transition-all"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>

          {/* Time Display */}
          <div className="flex justify-between text-white text-sm mb-4">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-6">
            <button 
              onClick={handleRewind}
              className="text-white hover:text-[#D4AF37] transition-colors"
            >
              <RotateCcw className="w-6 h-6" />
            </button>

            <button 
              onClick={togglePlay}
              className="bg-[#D4AF37] hover:bg-[#F4D03F] text-black rounded-full p-4 transition-colors shadow-lg"
            >
              {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
            </button>

            <button 
              onClick={handleForward}
              className="text-white hover:text-[#D4AF37] transition-colors"
            >
              <RotateCw className="w-6 h-6" />
            </button>

            <button 
              onClick={toggleMute}
              className="text-white hover:text-[#D4AF37] transition-colors"
            >
              {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryVideo;
